import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  MenuItem,
  Paper,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Avatar,
  CircularProgress,
  OutlinedInput,
} from "@mui/material";
import {
  AccountCircle,
  Phone,
  Lock,
  School,
  Business,
  CalendarMonth,
  Code,
  LinkedIn,
  Cake,
  Interests,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useDropzone } from "react-dropzone";
import { interestedCategoriesOptions } from "../Constant/InterestedCategoriesOptions";
import { learningGoalsOptions } from "../Constant/LearningGoalsOptions";
import { useDispatch, useSelector } from "react-redux";
import { getCountries } from "../../State/Country/Action";
import {
  getCurrentLearner,
  resetAllActions,
  updateLearner,
} from "../../State/Learner/Action";
import { useNavigate } from "react-router-dom";

const LearnerRegistration = () => {
  const dispatch = useDispatch();

  const learner = useSelector(
    (state) => state.learner?.currentLearner?.data || {}
  );

  const updateLearnerResponse = useSelector(
    (state) => state.learner?.updateLearnerResponse?.data || {}
  );

  const countries = useSelector(
    (state) => state.country?.countriesResponse?.data || []
  );

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    username: "",
    password: "",
    countryId: "",
    preferredLanguage: "",
    interestedCategories: [],
    learningGoal: "",
    profilePic: null,
    industryExperience: "",
    highestQualification: "",
    yearOfQualification: "",
    dateOfBirth: "",
    githubLink: "",
    linkedInUrl: "",
  });

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(resetAllActions());
    dispatch(getCountries());
    dispatch(getCurrentLearner());
  }, [dispatch]);

  useEffect(() => {
    if (updateLearnerResponse?.success) {
      navigate("/profile-detail");
    }
  }, [updateLearnerResponse]);

  useEffect(() => {
    if (learner && Object.keys(learner).length > 0) {
      const matchedCountry = countries.find((c) => c.name === learner.country);
      setFormData({
        fullName: learner.fullName || "",
        phone: learner.phone || "",
        username: learner.username || "",
        password: learner.password || "",
        countryId: matchedCountry?.id || "",
        preferredLanguage: learner.preferredLanguage || "",
        interestedCategories: learner.interestedCategories || [],
        learningGoal: learner.learningGoal || "",
        profilePic: learner.profilePictureUrl || null,
        industryExperience: learner.industryExperience || "",
        highestQualification: learner.highestQualification || "",
        yearOfQualification: learner.yearOfQualification || "",
        dateOfBirth: learner.dateOfBirth || "",
        githubLink: learner.githubLink || "",
        linkedInUrl: learner.linkedInUrl || "",
      });
      setPreviewUrl(learner.profilePictureUrl || "");
    }
  }, [learner, countries]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectChange = (e) => {
    const {
      target: { value },
    } = e;
    setFormData((prev) => ({
      ...prev,
      interestedCategories:
        typeof value === "string" ? value.split(",") : value,
    }));
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles?.length > 0) {
      const uploadedFile = acceptedFiles[0];
      setFile(uploadedFile);
      setPreviewUrl(URL.createObjectURL(uploadedFile));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const formPayload = new FormData();
    if (file) formPayload.append("file", file);
    formPayload.append(
      "learner",
      new Blob([JSON.stringify(formData)], { type: "application/json" })
    );
    dispatch(updateLearner(formPayload));
  };

  const renderTextField = (
    name,
    label,
    type = "text",
    icon = <AccountCircle />
  ) => (
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label={label}
        name={name}
        type={type}
        value={formData[name]}
        onChange={handleChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">{icon}</InputAdornment>
          ),
        }}
      />
    </Grid>
  );

  return (
    <Box
      sx={{
        background: "linear-gradient(to right, #fce4ec, #e3f2fd)",
        minHeight: "100vh",
        py: 6,
        px: 2,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper
          elevation={6}
          sx={{
            p: 4,
            maxWidth: 900,
            mx: "auto",
            borderRadius: 4,
            backgroundColor: "#fffefecc",
          }}
        >
          <Typography variant="h4" align="center" gutterBottom>
            🎓 Learner Profile
          </Typography>

          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              p: 2,
              mb: 3,
              textAlign: "center",
              backgroundColor: isDragActive ? "#f0f0f0" : "#fafafa",
              cursor: "pointer",
            }}
          >
            <input {...getInputProps()} />
            {uploading ? (
              <CircularProgress size={24} />
            ) : previewUrl ? (
              <Avatar
                src={previewUrl}
                sx={{ width: 100, height: 100, mx: "auto", mb: 1 }}
              />
            ) : (
              <Typography>
                📤 Drag & drop your profile picture, or click to select
              </Typography>
            )}
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {renderTextField("fullName", "Full Name")}
              {renderTextField("phone", "Phone", "text", <Phone />)}
              {renderTextField("username", "Username")}
              {renderTextField("password", "Password", "password", <Lock />)}
              {renderTextField(
                "dateOfBirth",
                "Date of Birth",
                "date",
                <Cake />
              )}
              {renderTextField(
                "highestQualification",
                "Qualification",
                "text",
                <School />
              )}
              {renderTextField(
                "yearOfQualification",
                "Qualification Year",
                "number",
                <CalendarMonth />
              )}
              {renderTextField(
                "industryExperience",
                "Industry Experience",
                "text",
                <Business />
              )}
              {renderTextField("githubLink", "GitHub", "text", <Code />)}
              {renderTextField("linkedInUrl", "LinkedIn", "text", <LinkedIn />)}

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select
                    name="countryId"
                    value={formData.countryId}
                    onChange={handleChange}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.id} value={country.id}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Preferred Language</InputLabel>
                  <Select
                    name="preferredLanguage"
                    value={formData.preferredLanguage}
                    onChange={handleChange}
                  >
                    {["English", "Hindi", "Spanish", "French"].map((lang) => (
                      <MenuItem key={lang} value={lang}>
                        {lang}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Learning Goal</InputLabel>
                  <Select
                    name="learningGoal"
                    value={formData.learningGoal}
                    onChange={handleChange}
                  >
                    {learningGoalsOptions.map(({ category, options }) => [
                      <MenuItem
                        key={`cat-${category}`}
                        disabled
                        sx={{
                          backgroundColor: "#e0e0e0",
                          justifyContent: "center",
                        }}
                      >
                        {category}
                      </MenuItem>,
                      ...options.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      )),
                    ])}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Interested Categories</InputLabel>
                  <Select
                    multiple
                    name="interestedCategories"
                    value={formData.interestedCategories}
                    onChange={handleMultiSelectChange}
                    input={<OutlinedInput label="Interested Categories" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            icon={<Interests />}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {interestedCategoriesOptions.map(
                      ({ category, options }) => [
                        <MenuItem
                          key={`cat-${category}`}
                          disabled
                          sx={{
                            backgroundColor: "#e0e0e0",
                            justifyContent: "center",
                          }}
                        >
                          {category}
                        </MenuItem>,
                        ...options.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        )),
                      ]
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box mt={4} textAlign="center">
              <motion.div whileHover={{ scale: 1.05 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                >
                  💾 Save Changes
                </Button>
              </motion.div>
            </Box>
          </form>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default LearnerRegistration;
